import type { SpacePickerProps, OperationFilter } from '@atlassian/confluence-space-picker';
import type { IntlShape } from 'react-intl-next';

import { i18n } from './smartButtonFormData-i18n.ts';

export interface LabelPickerOptionType {
	id?: string;
	name?: string;
	prefix?: string;
}

export type LabelPickerValue = {
	id: string;
	prefix: string | null;
	name: string | null;
};

export interface ContentState {
	color?: string;
	id: string;
	label: string;
}

export enum ValueType {
	ID = 'ID',
	NAME = 'NAME',
	COPY = 'COPY',
	ADD = 'ADD',
	REFERENCE = 'REFERENCE',
	SMART = 'SMART',
	SAME = 'SAME',
	FREE = 'FREE',
	AUTO = 'AUTO',
	CLEAR = 'CLEAR',
	ANY = 'ANY',
	GROUP_ID = 'GROUP_ID',
}

export interface SimpleValue {
	additional?: string;
	type: ValueType;
	value: string;
}

export enum ConfluenceContentType {
	page = 'page',
	blogpost = 'blogpost',
	attachment = 'attachment',
	content = 'content',
	comment = 'comment',
	embed = 'embed',
	whiteboard = 'whiteboard',
}

export interface User {
	accountId: string;
	emailAddress?: string;
	fullName?: string;
	name?: string;
	publicName?: string;
	timeZone?: string;
}

export interface BaseContent {
	author?: User;
	id: string;
	type?: ConfluenceContentType;
	url?: string;
}

export interface Label {
	contentId: string;
	id: string;
	name: string;
	url: string;
}

export interface Content extends BaseContent {
	dateFirstPublished?: Date;
	dateLastUpdated?: Date;
	labels?: Label[];
	parent?: Content;
	state?: string;
	status?: string;
	title: string;
}

export interface Space {
	containsExternalCollaborators: boolean;
	iconUrl?: string;
	id: string;
	isUnknownSpace?: boolean;
	key: string;
	label: string;
}

export type SpacePickerValue = Partial<Space> & Required<Pick<Space, 'id'>>;

export interface NestedSpacePickerType extends Omit<
	SpacePickerProps,
	'value' | 'onChange' | 'client'
> {
	name: string;
	label?: string;
	value?: Space;
	onChange: (value: Space | undefined) => void;
	customOptions?: Space[];
	isRequired?: boolean;
	operationCheckFilters?: OperationFilter[];
}

export type PickerWithSpace<T> = {
	name: string;
	label?: string;
	value?: T;
	onChange: (val: T | undefined) => void;
	isRequired?: boolean;
	siteUrl?: string;
	spacePickerProps: NestedSpacePickerType;
	classNamePrefix: string;
	isDisabled: boolean;
	spacePickerError?: string;
};

export const getSameSpaceAsTriggerOption = (intl: IntlShape): Space => ({
	id: 'same-space-as-trigger',
	key: 'same-space-as-trigger',
	label: intl.formatMessage(i18n.sameSpaceAsTrigger),
	containsExternalCollaborators: false,
	isUnknownSpace: true,
});

export interface PageTemplate {
	iconUrl?: string;
	id: string;
	isBlueprint?: boolean;
	label?: string;
}

export enum ContentRestrictionType {
	ANYONE_CAN_VIEW_AND_EDIT = 'ANYONE_CAN_VIEW_AND_EDIT',
	ANYONE_CAN_VIEW_RESTRICTED_EDIT = 'ANYONE_CAN_VIEW_RESTRICTED_EDIT',
	RESTRICTED_VIEW_AND_EDIT = 'RESTRICTED_VIEW_AND_EDIT',
}

export type ContentRestrictionOption = {
	value: ContentRestrictionType;
	label: () => string;
};

export const contentRestrictionOptions: ContentRestrictionOption[] = [
	{
		value: ContentRestrictionType.ANYONE_CAN_VIEW_AND_EDIT,
		label: () => 'Anyone in the space can view and edit',
	},
	{
		value: ContentRestrictionType.ANYONE_CAN_VIEW_RESTRICTED_EDIT,
		label: () => 'Anyone in the space can view, only some can edit',
	},
	{
		value: ContentRestrictionType.RESTRICTED_VIEW_AND_EDIT,
		label: () => 'Only specific people can view or edit',
	},
];

/**
 * types from react-select
 */
interface OptionTypeBase {
	[key: string]: any;
}
type OptionsType<OptionType extends OptionTypeBase> = ReadonlyArray<OptionType>;

export type OptionValueType<OptionType extends OptionTypeBase, IsMulti extends boolean> =
	| (IsMulti extends true ? OptionsType<OptionType> : OptionType)
	| null
	| undefined;

export type ClassificationOption = {
	label: string;
	value?: string;
};

export interface DataClassificationOption {
	id?: string;
}

export type ClassificationOptions = ClassificationOption[];

export type ClassificationLevel = {
	name: string;
	id: string;
	order: number;
	color: string;
	description: string;
	guideline: string;
	status: string;
};

// This is cut down from the original type in barrel since we only need these fields in the design
export interface OutgoingEmailActionConfig {
	body?: string;
	subject?: string;
	to?: SimpleValue[];
}

export interface OutgoingEmailConfigError {
	addresseePickerError?: string;
}
export interface CreatePageActionConfig {
	destinationPageTitle: string;
	destinationParentPage?: Content;
	destinationSpace: Space;
	template?: PageTemplate;
	templateSpace?: Space;
}

export interface CreatePageConfigError {
	destinationPageTitleError?: string;
}

export interface RestrictPageActionConfig {
	contentRestrictionType: ContentRestrictionType;
	readOperationAccountIds: string[];
	readOperationGroupIds: string[];
	updateOperationAccountIds: string[];
	updateOperationGroupIds: string[];
}

export interface RestrictPageConfigError {
	updaterPickerConfigError?: string;
}

export interface ChangePageOwnerActionConfig {
	ownerId: string;
}

export interface ChangePageOwnerConfigError {
	pageOwnerPickerError?: string;
}

export interface ChangePageStatusActionConfig {
	pageStatus: ContentState;
}

export interface ChangePageStatusConfigError {
	contentStatePickerConfigError?: string;
}

export interface CopyPageActionConfig {
	destinationPageTitle: string;
	destinationParentPage?: Content;
	destinationSpace: Space;
}

export interface CopyPageConfigError {
	destinationPageTitleError?: string;
}

export interface MovePageActionConfig {
	destinationParentPage?: Content;
	destinationSpace: Space;
}

export interface MovePageConfigError {
	destinationSpaceError?: string;
}

export interface AddLabelActionConfig {
	labelPicker: LabelPickerOptionType[];
}

export interface AddLabelConfigError {
	labelPickerError?: string;
}

export interface AddCommentActionConfig {
	commentText: string;
}

export interface AddCommentConfigError {
	commentTextError?: string;
}

export interface UpdateDataClassificationActionConfig {
	dataClassificationOption: DataClassificationOption;
}

export enum IssueFieldValueType {
	ID = 'ID',
}

export enum IssueFieldOperation {
	SET = 'SET',
	ADD = 'ADD',
	ADDREMOVE = 'ADDREMOVE',
	REMOVE = 'REMOVE',
	COPY = 'COPY',
}

export type ProjectIssueValue = {
	type: IssueFieldValueType;
	value: string;
};

export type JiraIssueFieldValue = ProjectIssueValue | string;

export interface JiraIssueField {
	field: JiraIssueFieldValue;
	fieldType: string;
	type: IssueFieldOperation;
	value: JiraIssueFieldValue | string;
}

export interface CreateJiraIssueConfig {
	connection: JiraConnection;
	operations: JiraIssueField[];
	// Used for updating to an existing connection
	connectionId?: string;
}

export interface JiraConnection {
	cloudId?: string;
	baseUrl?: string;
}

export interface UpdateDataClassificationConfigError {
	dataClassificationOptionError?: string;
}

export interface GenerateAISummaryActionConfig {
	inputSmartValue: string;
}

export interface GenerateAISummaryConfigError {
	inputSmartValueError?: string;
}

export interface GenerateAIActionItemsActionConfig {
	inputSmartValue: string;
}

export interface GenerateAIActionItemsConfigError {
	inputSmartValueError?: string;
}

export interface CreateDatabaseActionConfig {
	destinationDatabaseTitle: string;
	destinationParentContent?: Content;
	destinationSpace: Space;
}

export interface CreateDatabaseConfigError {
	destinationDatabaseTitleError?: string;
}

export interface CreateWhiteboardActionConfig {
	destinationWhiteboardTitle: string;
	destinationParentContent?: Content;
	destinationSpace: Space;
}

export interface CreateWhiteboardConfigError {
	destinationWhiteboardTitleError?: string;
}

export interface CreateFolderActionConfig {
	destinationFolderTitle: string;
	destinationParentContent?: Content;
	destinationSpace: Space;
}

export interface CreateFolderConfigError {
	destinationFolderTitleError?: string;
}

export interface CreateJiraIssueConfigError {
	projectIdError?: string;
	issueTypeError?: string;
}

export interface RovoConnection {
	cloudId?: string;
}

export interface RovoAgentConfig {
	agentId: string;
	agentNamedId: string;
	agentInstruction: string;
}

export interface InvokeAgentActionConfig {
	connection: RovoConnection;
	agentActionConfig: RovoAgentConfig;
}

export interface InvokeAgentConfigError {
	agentIdError?: string;
	agentInstructionError?: string;
}

export interface ConfigErrors {
	restrictPageConfigError?: RestrictPageConfigError;
	changePageStatusConfigError?: ChangePageStatusConfigError;
	addLabelConfigError?: AddLabelConfigError;
	createPageConfigError?: CreatePageConfigError;
	changePageOwnerConfigError?: ChangePageOwnerConfigError;
	movePageConfigError?: MovePageConfigError;
	outgoingEmailConfigError?: OutgoingEmailConfigError;
	copyPageConfigError?: CopyPageConfigError;
	updateDataClassificationConfigError?: UpdateDataClassificationConfigError;
	generateAISummaryConfigError?: GenerateAISummaryConfigError;
	generateAIActionItemsConfigError?: GenerateAIActionItemsConfigError;
	createDatabaseConfigError?: CreateDatabaseConfigError;
	createWhiteboardConfigError?: CreateWhiteboardConfigError;
	createFolderConfigError?: CreateFolderConfigError;
	createJiraIssueConfigError?: CreateJiraIssueConfigError;
	invokeAgentConfigError?: InvokeAgentConfigError;
	addCommentConfigError?: AddCommentConfigError;
}

export enum TemplateId {
	changePageStatusAndSendEmail = 'confluence_end_user_template_1',
	publishNewPageAndAddLabel = 'confluence_end_user_template_2',
	addLabelAndChangePageStatus = 'confluence_end_user_template_3',
	changePageRestrictionsAndSendEmail = 'confluence_end_user_template_4',
	changePageOwnerAndSendEmail = 'confluence_end_user_template_5',
	// 6 is listed on the project page but not designs
	changePageStatusAndRestrictionsAndSendEmail = 'confluence_end_user_template_6',
	copyPageAndChangeOwnerAndStatusAndRestrictions = 'confluence_end_user_template_7',
	publishPageAndChangeOwnerAndStatusAndRestrictions = 'confluence_end_user_template_8',
	// new templates
	movePageAndAddLabel = 'confluence_end_user_template_9',
	updateDataClassificationAndChangeRestrictionsAndSendEmail = 'confluence_end_user_template_10',
	summarizeWithAIAndGenerateAIActionItemsAndSendEmail = 'confluence_end_user_template_11',
	createDatabaseAndSendEmail = 'confluence_end_user_template_12',
	createWhiteboardAndSendEmail = 'confluence_end_user_template_13',
	publishNewPageAddLabelCreateJiraIssue = 'confluence_end_user_template_14',
	createFolderAndPublishNewPage = 'confluence_end_user_template_15',
	// new rovo templates
	invokeAgentAndPublishNewPage = 'confluence_end_user_template_16',
	invokeAgentAndSendEmail = 'confluence_end_user_template_17',
	invokeAgentAndAddComment = 'confluence_end_user_template_18',
}

// Templates that use AI
export const AITemplateIds = [
	TemplateId.summarizeWithAIAndGenerateAIActionItemsAndSendEmail,
	TemplateId.invokeAgentAndPublishNewPage,
	TemplateId.invokeAgentAndSendEmail,
	TemplateId.invokeAgentAndAddComment,
];

export enum ContentStateRestrictionLevel {
	NONE = 'NONE',
	PAGE_OWNER = 'PAGE_OWNER',
}
